import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Sun, Moon, List, X, Trophy, SignOut, Gauge, Medal } from '@phosphor-icons/react';
import { useState } from 'react';
import TopicSearch from '../TopicSearch';

export default function Header() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isStudent = user && user.role === 'student';
  const showStudentTools = user && user.role !== 'parent';
  const showCommercialNav = !user || user.role !== 'student';

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b-2 border-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <img src="/grade9ready-logo.png" alt="Grade9Ready" className="h-10 w-auto" />
            <span className="font-heading font-bold text-xl hidden sm:block">Grade9Ready</span>
          </Link>

          {/* Desktop Nav — student / parent / commercial intent */}
          <nav className="hidden lg:flex items-center gap-5">
            <Link to="/subjects" className="font-medium hover:text-primary transition-colors" data-testid="nav-subjects">
              Subjects
            </Link>
            {showCommercialNav && (
              <>
                <Link to="/for-parents" className="font-medium hover:text-primary transition-colors" data-testid="nav-parents">
                  Parents
                </Link>
                <Link to="/pricing" className="font-medium hover:text-primary transition-colors" data-testid="nav-pricing">
                  Pricing
                </Link>
                <Link to="/free-resources" className="font-medium hover:text-primary transition-colors" data-testid="nav-free-resources">
                  Free Resources
                </Link>
              </>
            )}
          </nav>

          {/* Topic Search (desktop, students & admin) */}
          {showStudentTools && (
            <div className="hidden md:block flex-1 max-w-xs">
              <TopicSearch placeholder="Search topics..." />
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg border-2 border-foreground bg-card hover:bg-secondary transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={20} weight="bold" /> : <Sun size={20} weight="bold" />}
            </button>

            {/* User menu or auth buttons */}
            {user === null ? (
              <div className="w-8 h-8 rounded-full bg-secondary animate-pulse" />
            ) : user ? (
              <div className="hidden md:flex items-center gap-2">
                {/* Achievements (students only) */}
                {isStudent && (
                  <Link
                    to="/achievements"
                    className="flex items-center gap-1 px-3 py-1 bg-accent rounded-full border-2 border-foreground text-sm font-bold hover:-translate-y-0.5 transition-transform"
                    data-testid="nav-achievements"
                    aria-label="Achievements"
                    title="Achievements"
                  >
                    <Medal size={16} weight="fill" />
                    <span className="hidden lg:inline">{user.xp || 0} XP</span>
                    <span className="lg:hidden">{user.xp || 0}</span>
                  </Link>
                )}

                {/* Dashboard link */}
                <Link
                  to={user.role === 'parent' ? '/parent/dashboard' : user.role === 'admin' ? '/admin' : '/dashboard'}
                  className="neo-btn-secondary !px-3 !py-2 text-sm flex items-center gap-1.5"
                  data-testid="dashboard-link"
                >
                  <Gauge size={18} weight="bold" />
                  <span className="hidden lg:inline">Dashboard</span>
                </Link>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg border-2 border-foreground bg-card hover:bg-destructive hover:text-white transition-colors"
                  data-testid="logout-btn"
                  aria-label="Logout"
                >
                  <SignOut size={20} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <Link to="/login" className="font-medium hover:text-primary transition-colors" data-testid="login-link">
                  Log in
                </Link>
                <Link to="/signup" className="neo-btn !px-4 !py-2 text-sm" data-testid="signup-link">
                  Create Parent Account
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg border-2 border-foreground bg-card"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t-2 border-foreground bg-card">
          <nav className="px-4 py-4 space-y-3">
            {showStudentTools && (
              <div data-testid="mobile-topic-search-wrapper">
                <TopicSearch placeholder="Search topics..." fullWidth onNavigate={() => setMobileMenuOpen(false)} />
              </div>
            )}
            <Link to="/subjects" className="block font-medium py-2" onClick={() => setMobileMenuOpen(false)} data-testid="nav-subjects-mobile">
              Subjects
            </Link>
            {isStudent && (
              <Link
                to="/achievements"
                className="flex items-center gap-2 font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="nav-achievements-mobile"
              >
                <Medal size={18} weight="fill" className="text-accent" /> Achievements
              </Link>
            )}
            {showCommercialNav && (
              <>
                <Link to="/for-parents" className="block font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                  Parents
                </Link>
                <Link to="/pricing" className="block font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                  Pricing
                </Link>
                <Link to="/free-resources" className="block font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                  Free Resources
                </Link>
              </>
            )}
            <Link to="/about" className="block font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
              About
            </Link>

            {user ? (
              <>
                <div className="flex items-center gap-2 py-2">
                  <Trophy size={18} weight="fill" className="text-accent" />
                  <span className="font-bold">{user.xp || 0} XP</span>
                </div>
                <Link
                  to={user.role === 'parent' ? '/parent/dashboard' : user.role === 'admin' ? '/admin' : '/dashboard'}
                  className="block neo-btn-secondary text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="w-full neo-btn-secondary !bg-destructive !text-white">
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block neo-btn-secondary text-center" onClick={() => setMobileMenuOpen(false)}>
                  Log in
                </Link>
                <Link to="/signup" className="block neo-btn text-center" onClick={() => setMobileMenuOpen(false)}>
                  Create Parent Account
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
